<template>
  <div class="agent-sidebar">
    <!-- 顶栏：状态 + 关闭 -->
    <div class="agent-header">
      <div class="agent-title">
        <el-icon size="20" color="#409EFF"><Monitor /></el-icon>
        <span>对话助手</span>
        <el-tag v-if="isProcessing" size="small" type="warning" effect="plain" class="working-tag">
          {{ workingLabel || 'Working' }}
        </el-tag>
        <el-tag v-else-if="currentTask?.status === 'failed'" size="small" type="danger" effect="plain">
          已中断
        </el-tag>
      </div>
      <el-button circle :icon="Close" size="small" @click="$emit('close')" />
    </div>

    <!-- 会话列表 -->
    <div class="agent-tasks-toolbar">
      <el-button type="primary" size="small" @click="onNewChat">新对话</el-button>
    </div>
    <div class="agent-tasks">
      <div
        v-for="task in tasks"
        :key="task.id"
        :class="['task-item', { active: currentTaskId === task.id }]"
        @click="onSelectTask(task.id)"
      >
        <div class="task-header">
          <span class="task-title">{{ task.title }}</span>
          <div class="task-actions">
            <el-tag :type="getStatusType(task.status)" size="small">{{ task.status }}</el-tag>
            <el-button
              circle
              size="small"
              text
              :icon="Delete"
              class="task-delete-btn"
              title="删除会话"
              @click.stop="onDeleteTask(task.id)"
            />
          </div>
        </div>
        <div class="task-time">{{ formatTime(task.time) }}</div>
      </div>
    </div>

    <!-- Tabs: 对话 | 步骤 | 日志 -->
    <div class="agent-tabs">
      <button :class="{ active: activeTab === 'chat' }" @click="activeTab = 'chat'">对话</button>
      <button :class="{ active: activeTab === 'steps' }" @click="activeTab = 'steps'">
        步骤{{ stepsCount ? `(${stepsCount})` : '' }}
      </button>
      <button :class="{ active: activeTab === 'logs' }" @click="activeTab = 'logs'">日志</button>
    </div>

    <!-- 对话 -->
    <div v-show="activeTab === 'chat'" class="agent-messages" ref="messagesRef">
      <div v-if="currentTask && currentTask.messages.length === 0" class="welcome">
        <el-icon size="40" color="#409EFF"><Monitor /></el-icon>
        <p>对话助手：能读取你当前页面的内容，问进度、查矩阵、批量发布。账号策略与定时调研在左侧「自动化」。</p>
        <div class="quick-actions">
          <el-button size="small" @click="sendQuick('总结一下当前页面的内容，并告诉我可以做什么')">处理当前页</el-button>
          <el-button size="small" @click="sendQuick('把当前页选中（没选就是页面上审核通过）的文章发布到对应平台的全部可用账号，发完告诉我进度')">发布当前页</el-button>
          <el-button size="small" @click="sendQuick('帮我看看最近内容任务进度怎么样')">任务进度</el-button>
          <el-button size="small" @click="sendQuick('先列 Todo：调研账号 demo，产出选题并加入日历草稿')">多步运营</el-button>
        </div>
      </div>

      <template v-for="(msg, idx) in currentTask?.messages" :key="idx">
        <div v-if="msg.role === 'user'" class="message user">
          <div class="message-content">{{ msg.content }}</div>
        </div>

        <div v-if="msg.role === 'agent'" class="message agent">
          <div class="message-content">
            <AgentMessageParts
              v-if="msg.parts?.length || msg.streaming"
              :parts="msg.parts || []"
              :streaming="!!msg.streaming"
            />
            <div v-else-if="msg.steps?.length" class="steps">
              <div
                v-for="(step, sIdx) in msg.steps"
                :key="sIdx"
                :class="['step', `step-${step.status}`]"
              >
                <el-icon class="step-icon"><component :is="getStepIcon(step.status)" /></el-icon>
                <span class="step-name">{{ step.name }}</span>
                <span v-if="step.result" class="step-result">{{ step.result }}</span>
              </div>
            </div>
            <div v-if="!msg.parts?.length && msg.text" class="text" v-html="renderMarkdown(msg.text)" />

            <div v-if="msg.articles?.length" class="articles">
              <div v-for="article in msg.articles" :key="article.id" class="article-card">
                <h4>{{ article.title }}</h4>
                <div class="article-tags">
                  <el-tag size="small">{{ article.platform }}</el-tag>
                  <el-tag size="small" type="info">{{ article.article_type }}</el-tag>
                </div>
                <div class="article-actions">
                  <el-button size="small" @click="$emit('view-article', article)">查看</el-button>
                  <el-button size="small" type="primary" @click="copyText(article.content)">复制</el-button>
                </div>
              </div>
            </div>

            <div v-if="msg.canResume && !isProcessing" class="resume-row">
              <el-button size="small" type="warning" plain @click="onContinue">继续执行</el-button>
            </div>
          </div>
        </div>
      </template>

      <div
        v-if="isProcessing && !currentTask?.messages?.some((m) => m.role === 'agent' && m.streaming)"
        class="message agent"
      >
        <div class="typing"><span></span><span></span><span></span></div>
      </div>
    </div>

    <!-- 步骤 -->
    <div v-show="activeTab === 'steps'" class="agent-panel-body">
      <div class="panel-section">
        <div class="panel-title">待办</div>
        <div v-if="!(currentTodos || []).length" class="panel-empty">暂无 Todo</div>
        <ul v-else class="todo-list">
          <li v-for="t in currentTodos" :key="t.id" :class="{ done: t.done }">
            <span class="todo-check">{{ t.done ? '✓' : '○' }}</span>
            <span>{{ t.text }}</span>
          </li>
        </ul>
      </div>
      <div class="panel-section">
        <div class="panel-title">工具时间线</div>
        <div v-if="!toolTimeline.length" class="panel-empty">本轮尚无工具调用</div>
        <div v-for="(it, i) in toolTimeline" :key="i" class="timeline-item">
          <span :class="['dot', it.status]" />
          <div>
            <div class="tl-name">{{ it.label || it.name }}</div>
            <div v-if="it.summary" class="tl-summary">{{ it.summary }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 日志 -->
    <div v-show="activeTab === 'logs'" class="agent-panel-body logs-body">
      <div v-if="!(currentLogs || []).length" class="panel-empty">暂无日志</div>
      <div v-for="(log, i) in currentLogs" :key="i" class="log-line">
        <span class="log-type">{{ log.type }}</span>
        <span class="log-body">{{ formatLog(log) }}</span>
      </div>
    </div>

    <!-- 底部输入 + Auto -->
    <div class="agent-input">
      <div class="input-toolbar">
        <div class="auto-switch">
          <span>Auto</span>
          <el-switch v-model="autoMode" size="small" />
          <span class="auto-hint">{{ autoMode ? '低风险自动' : '写操作需审批' }}</span>
        </div>
        <div class="auto-switch" title="开启后 Agent 发布文章时直接入队，不再逐条审批">
          <span>自动发布</span>
          <el-switch
            v-model="autoPublish"
            size="small"
            style="--el-switch-on-color: #e6a23c"
            :before-change="confirmAutoPublish"
          />
        </div>
        <el-button
          v-if="!isProcessing && (currentTask?.status === 'failed' || currentTask?.status === 'interrupted')"
          size="small"
          type="warning"
          plain
          @click="onContinue"
        >
          继续执行
        </el-button>
      </div>
      <div class="page-context-row">
        <el-checkbox v-model="sendPageContext" size="small">附带当前页面</el-checkbox>
        <el-tag v-if="sendPageContext && pageLabel.name" size="small" effect="plain" class="page-tag">
          {{ pageLabel.name }}{{ pageLabel.detail ? ` · ${pageLabel.detail}` : '' }}
        </el-tag>
      </div>
      <div class="input-row">
        <el-input
          v-model="inputMessage"
          type="textarea"
          :rows="3"
          placeholder="例如：把这页选中的文章发到全部小红书账号（Enter 发送，Shift+Enter 换行）"
          @keydown.enter="onInputEnter"
        />
        <el-button v-if="isProcessing" @click="stopStream">停止</el-button>
        <el-button type="primary" :icon="Promotion" :loading="isProcessing" @click="sendMessage">
          发送
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, nextTick, onMounted, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  Close, Promotion, Loading, Check, Warning, Clock, Delete, Monitor,
} from '@element-plus/icons-vue'
import { useRoute } from 'vue-router'
import { useAgentStore } from '../stores/agentStore'
import { describePageContext } from '../stores/pageContext'
import AgentMessageParts from './agent/AgentMessageParts.vue'

export default {
  name: 'AgentSidebar',
  components: { AgentMessageParts, Monitor },
  emits: ['close', 'view-article'],
  setup() {
    const messagesRef = ref(null)
    const inputMessage = ref('')
    const agentStore = useAgentStore()
    const route = useRoute()
    const {
      tasks,
      currentTask,
      isProcessing,
      autoMode,
      autoPublish,
      sendPageContext,
      activeTab,
      workingLabel,
      currentTodos,
      currentLogs,
      stepsCount,
    } = agentStore

    const currentTaskId = computed(() => agentStore.currentTaskId.value)

    const toolTimeline = computed(() => {
      const msgs = currentTask.value?.messages || []
      const last = [...msgs].reverse().find((m) => m.role === 'agent')
      if (!last?.parts) return []
      return last.parts
        .filter((p) => p.kind === 'tool' && p.tool)
        .map((p) => ({
          name: p.tool.name,
          label: p.tool.label,
          status: p.tool.status,
          summary: p.tool.summary,
        }))
    })

    watch(
      () => currentTask.value?.messages?.length,
      () => scrollToBottom(),
    )
    watch(
      () => {
        const msgs = currentTask.value?.messages || []
        const last = msgs[msgs.length - 1]
        return last?.text?.length || last?.parts?.length || 0
      },
      () => scrollToBottom(),
    )

    const sendMessage = async () => {
      const text = inputMessage.value.trim()
      if (!text || isProcessing.value) return
      try {
        inputMessage.value = ''
        await agentStore.sendMessage(text)
        scrollToBottom()
      } catch (_) {
        /* store 已处理 */
      }
    }

    const onInputEnter = (e) => {
      // 中文输入法选词时的回车不发送；Shift+Enter 换行
      if (e.isComposing || e.keyCode === 229 || e.shiftKey) return
      e.preventDefault()
      sendMessage()
    }

    const pageLabel = computed(() => {
      void route.fullPath
      return describePageContext()
    })

    const confirmAutoPublish = async () => {
      if (autoPublish.value) return true
      try {
        await ElMessageBox.confirm(
          '开启后，Agent 调用发布工具时会直接把文章入队发布到矩阵账号，不再逐条弹出审批。确定开启？',
          '开启自动发布',
          { type: 'warning', confirmButtonText: '开启', cancelButtonText: '取消' },
        )
        return true
      } catch {
        return false
      }
    }

    const stopStream = () => agentStore.stopStream?.()

    const sendQuick = (text) => {
      inputMessage.value = text
      sendMessage()
    }

    const onContinue = async () => {
      try {
        await agentStore.continueRun()
        scrollToBottom()
      } catch (_) {
        /* ignore */
      }
    }

    const onSelectTask = async (id) => {
      await agentStore.selectTask(id)
    }

    const onNewChat = async () => {
      await agentStore.createTask('新对话')
      await nextTick()
      scrollToBottom()
    }

    const onDeleteTask = async (id) => {
      try {
        await ElMessageBox.confirm('确定删除这个对话吗？删除后无法恢复。', '删除会话', {
          type: 'warning',
          confirmButtonText: '删除',
          cancelButtonText: '取消',
        })
      } catch {
        return
      }
      await agentStore.deleteTask(id)
      if (tasks.value.length === 0) {
        await agentStore.createTask('新对话')
      }
      ElMessage.success('会话已删除')
    }

    const scrollToBottom = () => {
      nextTick(() => {
        if (messagesRef.value) {
          messagesRef.value.scrollTop = messagesRef.value.scrollHeight
        }
      })
    }

    const formatTime = (timestamp) =>
      new Date(timestamp).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })

    const getStatusType = (status) => {
      const map = {
        running: 'primary',
        completed: 'success',
        failed: 'danger',
        interrupted: 'warning',
        idle: 'info',
      }
      return map[status] || 'info'
    }

    const getStepIcon = (status) => {
      const map = {
        pending: Clock,
        running: Loading,
        completed: Check,
        failed: Warning,
      }
      return map[status] || Clock
    }

    const renderMarkdown = (text) =>
      String(text || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`(.*?)`/g, '<code>$1</code>')
        .replace(/\n/g, '<br>')

    const formatLog = (log) => {
      if (!log) return ''
      if (log.type === 'turn') return `回合 ${log.index}/${log.maxTurns || '?'}`
      if (log.type === 'tool_start') return `开始 ${log.label || log.name}`
      if (log.type === 'tool_end' || log.type === 'tool')
        return `${log.name}: ${log.summary || log.status || ''}`
      if (log.type === 'usage') return JSON.stringify(log.usage || {})
      if (log.type === 'error') return log.error || ''
      if (log.type === 'approval_request') return `审批 ${log.name} (${log.approvalId})`
      try {
        return JSON.stringify(log)
      } catch {
        return String(log.type || '')
      }
    }

    const copyText = (text) => {
      navigator.clipboard.writeText(text)
      ElMessage.success('已复制到剪贴板')
    }

    onMounted(() => {
      agentStore.ensureSessions().catch(() => {})
    })

    return {
      messagesRef,
      inputMessage,
      tasks,
      currentTask,
      currentTaskId,
      isProcessing,
      autoMode,
      autoPublish,
      sendPageContext,
      pageLabel,
      confirmAutoPublish,
      onInputEnter,
      activeTab,
      workingLabel,
      currentTodos,
      currentLogs,
      stepsCount,
      toolTimeline,
      sendMessage,
      stopStream,
      sendQuick,
      onContinue,
      onSelectTask,
      onNewChat,
      onDeleteTask,
      formatTime,
      getStatusType,
      getStepIcon,
      renderMarkdown,
      formatLog,
      copyText,
      Close,
      Promotion,
      Delete,
      Monitor,
    }
  },
}
</script>

<style scoped>
.agent-sidebar {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: #fafbfc;
}

.agent-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid #e4e7ed;
  background: #fff;
}

.agent-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
}

.working-tag {
  font-variant-numeric: tabular-nums;
}

.agent-tasks-toolbar {
  padding: 8px 12px;
  border-bottom: 1px solid #e4e7ed;
  background: #fff;
}

.agent-tasks {
  max-height: 100px;
  overflow-y: auto;
  border-bottom: 1px solid #e4e7ed;
  padding: 6px 8px;
  background: #fff;
}

.task-item {
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 2px;
}

.task-item:hover,
.task-item.active {
  background: #f5f7fa;
}

.task-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.task-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.task-delete-btn {
  opacity: 0;
  transition: opacity 0.15s ease;
}

.task-item:hover .task-delete-btn,
.task-item.active .task-delete-btn {
  opacity: 1;
}

.task-title {
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.task-time {
  font-size: 11px;
  color: #909399;
}

.agent-tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e4e7ed;
  background: #fff;
  padding: 0 8px;
}

.agent-tabs button {
  flex: 1;
  border: none;
  background: transparent;
  padding: 10px 8px;
  font-size: 13px;
  color: #606266;
  cursor: pointer;
  border-bottom: 2px solid transparent;
}

.agent-tabs button.active {
  color: #409eff;
  border-bottom-color: #409eff;
  font-weight: 600;
}

.agent-messages,
.agent-panel-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 14px;
}

.logs-body {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 11px;
}

.welcome {
  text-align: center;
  padding: 32px 16px;
  color: #909399;
}

.quick-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 14px;
  flex-wrap: wrap;
}

.message {
  margin-bottom: 14px;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message.user .message-content {
  background: #ecf5ff;
  margin-left: auto;
  max-width: 85%;
}

.message.agent .message-content {
  background: #fff;
  max-width: 98%;
  border: 1px solid #ebeef5;
}

.message-content {
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
}

.steps {
  margin-bottom: 12px;
}

.step {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  font-size: 13px;
}

.step.step-pending {
  color: #909399;
}
.step.step-running {
  color: #409eff;
}
.step.step-completed {
  color: #67c23a;
}
.step.step-failed {
  color: #f56c6c;
}

.step-name {
  flex: 1;
}
.step-result {
  font-size: 12px;
  color: #606266;
}

.articles {
  margin-top: 12px;
}

.article-card {
  background: #f5f7fa;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 8px;
}

.article-card h4 {
  margin: 0 0 8px;
  font-size: 14px;
}

.article-tags,
.article-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.resume-row {
  margin-top: 10px;
}

.typing {
  display: flex;
  gap: 4px;
  padding: 12px;
}

.typing span {
  width: 8px;
  height: 8px;
  background: #c0c4cc;
  border-radius: 50%;
  animation: typing 1.4s infinite;
}

.typing span:nth-child(2) {
  animation-delay: 0.2s;
}
.typing span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%,
  60%,
  100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-8px);
  }
}

.panel-section {
  margin-bottom: 18px;
}

.panel-title {
  font-size: 12px;
  font-weight: 600;
  color: #909399;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.panel-empty {
  font-size: 13px;
  color: #c0c4cc;
}

.todo-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.todo-list li {
  display: flex;
  gap: 8px;
  padding: 6px 0;
  font-size: 13px;
  color: #303133;
  border-bottom: 1px solid #f0f2f5;
}

.todo-list li.done {
  color: #909399;
  text-decoration: line-through;
}

.todo-check {
  width: 16px;
  color: #67c23a;
}

.timeline-item {
  display: flex;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f2f5;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 5px;
  background: #c0c4cc;
  flex-shrink: 0;
}
.dot.running {
  background: #409eff;
}
.dot.success {
  background: #67c23a;
}
.dot.error {
  background: #f56c6c;
}

.tl-name {
  font-size: 13px;
  color: #303133;
}
.tl-summary {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.log-line {
  display: flex;
  gap: 8px;
  padding: 4px 0;
  border-bottom: 1px solid #f5f7fa;
  word-break: break-all;
}

.log-type {
  color: #409eff;
  min-width: 72px;
  flex-shrink: 0;
}

.log-body {
  color: #606266;
}

.agent-input {
  flex-shrink: 0;
  border-top: 1px solid #e4e7ed;
  padding: 10px 12px;
  background: #fff;
}

.input-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 12px;
  margin-bottom: 8px;
}

.page-context-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin-bottom: 6px;
}

.page-tag {
  min-width: 0;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.auto-switch {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #606266;
}

.auto-hint {
  color: #909399;
  font-size: 11px;
}

.input-row {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.input-row .el-textarea {
  flex: 1;
}

.input-row .el-button {
  flex-shrink: 0;
  height: auto;
  min-height: 72px;
  padding: 0 16px;
}
</style>
